import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramVoltsAmpsOhms } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';

const KEY = 'tabai.calc.electricalVoltsAmpsOhms.v1';

type Fields = { voltage: string; current: string; resistance: string; power: string };
const EMPTY: Fields = { voltage: '', current: '', resistance: '', power: '' };

/**
 * Volts / Amps / Ohms — Ohm's Law + Power
 *   V = I × R,  P = V × I,  P = I² × R,  P = V² / R
 * Solves for whichever single field is missing given the other three (or any 2).
 */
const ElectricalVoltsAmpsOhmsScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill at least 2 of the 4 fields to calculate the missing ones.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const v = parseFloat(fields.voltage);
    const i = parseFloat(fields.current);
    const r = parseFloat(fields.resistance);
    const p = parseFloat(fields.power);

    if (fields.voltage === '') {
      if (fields.current && fields.resistance) {
        setSolvedFor('voltage');
        set({ voltage: (i * r).toFixed(2) });
      } else if (fields.power && fields.current) {
        if (i === 0) { setError('Current cannot be zero.'); return; }
        setSolvedFor('voltage');
        set({ voltage: (p / i).toFixed(2) });
      } else if (fields.power && fields.resistance) {
        if (r === 0) { setError('Resistance cannot be zero.'); return; }
        setSolvedFor('voltage');
        set({ voltage: Math.sqrt(p * r).toFixed(2) });
      }
    } else if (fields.current === '') {
      if (fields.voltage && fields.resistance) {
        if (r === 0) { setError('Resistance cannot be zero.'); return; }
        setSolvedFor('current');
        set({ current: (v / r).toFixed(3) });
      } else if (fields.power && fields.voltage) {
        if (v === 0) { setError('Voltage cannot be zero.'); return; }
        setSolvedFor('current');
        set({ current: (p / v).toFixed(3) });
      } else if (fields.power && fields.resistance) {
        if (r === 0) { setError('Resistance cannot be zero.'); return; }
        setSolvedFor('current');
        set({ current: Math.sqrt(p / r).toFixed(3) });
      }
    } else if (fields.resistance === '') {
      if (fields.voltage && fields.current) {
        if (i === 0) { setError('Current cannot be zero.'); return; }
        setSolvedFor('resistance');
        set({ resistance: (v / i).toFixed(2) });
      } else if (fields.voltage && fields.power) {
        if (p === 0) { setError('Power cannot be zero.'); return; }
        setSolvedFor('resistance');
        set({ resistance: (Math.pow(v, 2) / p).toFixed(2) });
      } else if (fields.current && fields.power) {
        if (i === 0) { setError('Current cannot be zero.'); return; }
        setSolvedFor('resistance');
        set({ resistance: (p / Math.pow(i, 2)).toFixed(2) });
      }
    } else if (fields.power === '') {
      if (fields.voltage && fields.current) {
        setSolvedFor('power');
        set({ power: (v * i).toFixed(2) });
      } else if (fields.voltage && fields.resistance) {
        if (r === 0) { setError('Resistance cannot be zero.'); return; }
        setSolvedFor('power');
        set({ power: (Math.pow(v, 2) / r).toFixed(2) });
      } else if (fields.current && fields.resistance) {
        setSolvedFor('power');
        set({ power: (Math.pow(i, 2) * r).toFixed(2) });
      }
    }
  }, [fields, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.voltage && { label: 'Voltage', value: fields.voltage, unit: 'V', emphasis: solvedFor === 'voltage' },
    fields.current && { label: 'Current', value: fields.current, unit: 'A', emphasis: solvedFor === 'current' },
    fields.resistance && { label: 'Resistance', value: fields.resistance, unit: 'Ω', emphasis: solvedFor === 'resistance' },
    fields.power && { label: 'Power', value: fields.power, unit: 'W', emphasis: solvedFor === 'power' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, solvedFor]);

  return (
    <CalcShell
      title="Volts / Amps / Ohms"
      subtitle="Calculate electrical relationships using Ohm's Law and Power formulas"
      formula={
        <>
          <FormulaLine>V = I × R</FormulaLine>
          <FormulaLine>P = V × I</FormulaLine>
          <FormulaLine>P = I² × R</FormulaLine>
          <FormulaLine>P = V² / R</FormulaLine>
          <FormulaNote>V = Voltage (V), I = Current (A), R = Resistance (Ω), P = Power (W)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formulas: V = I×R | P = V×I | P = I²×R | P = V²/R"
      diagram={<DiagramVoltsAmpsOhms />}
      outputSlots={{
        volts: fields.voltage ? parseFloat(fields.voltage) : undefined,
        amps: fields.current ? parseFloat(fields.current) : undefined,
        resistance_ohms: fields.resistance ? parseFloat(fields.resistance) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.volts != null && !fields.voltage) {
          patch.voltage = values.volts.toFixed(2);
        }
        if (values.amps != null && !fields.current) {
          patch.current = values.amps.toFixed(3);
        }
        if (values.resistance_ohms != null && !fields.resistance) {
          patch.resistance = values.resistance_ohms.toFixed(2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Voltage"
          value={fields.voltage}
          onChangeText={(v) => set({ voltage: v })}
          rightText="V"
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Current"
          value={fields.current}
          onChangeText={(v) => set({ current: v })}
          rightText="A"
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Resistance"
          value={fields.resistance}
          onChangeText={(v) => set({ resistance: v })}
          rightText="Ω"
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Power"
          value={fields.power}
          onChangeText={(v) => set({ power: v })}
          rightText="W"
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default ElectricalVoltsAmpsOhmsScreen;
